import { NATIONALITIES } from "../data/nationalities";
import type { MapperErgebnis, UebergangenerWert } from "./visitenkarteZuFormState";

/**
 * Übersetzt einen Personalausweis-Befund in die Feldschlüssel der
 * Personen-Anlage (`NewPrivateContactForm`).
 *
 * Nach demselben Muster wie `visitenkarteZuFormState`: Binding-Keys der Maske
 * (`postalcode` klein), katalog-gebundene Felder nur bei Treffer, alles andere
 * als `uebergangen` sichtbar statt still verworfen.
 *
 * Ausweisnummer, Gültigkeit, Ausstellungsdatum, Behörde und Dokumentart
 * standen bis 09/2026 unter einem Sonderrecht: Der Server nahm sie nur von den
 * Kontaktverantwortlichen und der Leitung entgegen, sonst verwarf er sie
 * kommentarlos. Der Mapper musste das spiegeln, damit ein Scan sie nicht
 * anbot, um sie beim Speichern wieder zu verlieren. Diese Sonderstellung hat
 * der PO am 21.09.2026 aufgehoben — die Felder werden immer übernommen.
 */

export type PersonalausweisBefund = Partial<{
  dokumentart: string;
  /** Nur Aufenthaltstitel: Art des Titels, z. B. „Niederlassungserlaubnis" (U6). */
  titelart: string;
  nachname: string;
  geburtsname: string;
  vornamen: string;
  geburtsdatum: string;
  geburtsort: string;
  staatsangehoerigkeit: string;
  geschlecht: string;
  ausweisnummer: string;
  gueltigBis: string;
  ausstellungsdatum: string;
  behoerde: string;
  strasse: string;
  plz: string;
  ort: string;
  mrz: string;
}> & {
  /** Welche Angaben die MRZ-Prüfziffer bestätigt hat. */
  geprueft?: string[];
};

export type AusweisKontext = {
  /** Anrede-Katalog (GET /contactsalutation). */
  anredeOptionen?: string[];
};

const gesetzt = (wert: string | undefined): wert is string => !!wert && wert.trim() !== "";

/**
 * "DEUTSCH" → "Deutsch". Gesucht wird im selben Katalog, den
 * `NationalitySelect` anbietet — ein Wert außerhalb davon würde im Feld
 * angezeigt, aber die Auswahl nicht treffen.
 */
export const staatsangehoerigkeitAusKatalog = (roh: string): string | undefined => {
  const wert = roh.trim().toLowerCase();
  if (!wert) return undefined;
  // Die MRZ nennt den ICAO-Code ("D" für Deutschland), der Klartext das
  // Adjektiv ("DEUTSCH") — beide Wege führen auf denselben Katalogeintrag.
  if (wert === "d" || wert === "deu" || wert === "deutsch") return "Deutsch";
  const treffer =
    NATIONALITIES.find((n) => n.adjective.toLowerCase() === wert) ??
    NATIONALITIES.find((n) => n.country.toLowerCase() === wert) ??
    NATIONALITIES.find((n) => n.code.toLowerCase() === wert);
  return treffer?.adjective;
};

/** Personalausweis → Personen-Maske. */
export const ausweisZuPrivatFormState = (
  befund: PersonalausweisBefund,
  kontext: AusweisKontext = {},
): MapperErgebnis => {
  const werte: Record<string, string> = {};
  const uebergangen: UebergangenerWert[] = [];

  if (gesetzt(befund.nachname)) werte.lastname = befund.nachname;
  // Alle Vornamen wie gedruckt — der Ausweis ist die amtliche Quelle, und wer
  // nur den Rufnamen will, kürzt ihn in der Maske.
  if (gesetzt(befund.vornamen)) werte.firstname = befund.vornamen;
  if (gesetzt(befund.geburtsname)) werte.birthname = befund.geburtsname;
  if (gesetzt(befund.geburtsort)) werte.placeofbirth = befund.geburtsort;
  if (gesetzt(befund.geburtsdatum)) werte.dateofbirth = befund.geburtsdatum;
  if (gesetzt(befund.strasse)) werte.street = befund.strasse;
  if (gesetzt(befund.plz)) werte.postalcode = befund.plz;
  if (gesetzt(befund.ort)) werte.city = befund.ort;

  if (gesetzt(befund.staatsangehoerigkeit)) {
    const treffer = staatsangehoerigkeitAusKatalog(befund.staatsangehoerigkeit);
    if (treffer) werte.citizenship = treffer;
    else
      uebergangen.push({
        feld: "Staatsangehörigkeit",
        wert: befund.staatsangehoerigkeit,
        grund: "steht nicht im Nationalitäten-Katalog",
      });
  }

  // Das Geschlechtsfeld des Ausweises hat in der Maske kein eigenes Ziel; die
  // Anrede ist das nächstliegende. Abgeleitet wird nur bei F und M — bei X
  // gibt es keine belastbare Zuordnung, und eine geratene Anrede ist eine
  // Anrede, die den Menschen falsch anspricht.
  if (gesetzt(befund.geschlecht)) {
    const anrede = befund.geschlecht === "F" ? "Frau" : befund.geschlecht === "M" ? "Herr" : null;
    const imKatalog =
      anrede &&
      kontext.anredeOptionen?.find((o) => o.trim().toLowerCase() === anrede.toLowerCase());
    if (imKatalog) werte.anrede = imKatalog;
    else
      uebergangen.push({
        feld: "Geschlecht",
        wert: befund.geschlecht,
        grund: anrede
          ? "„" + anrede + "\" steht nicht im Anrede-Katalog"
          : "daraus lässt sich keine Anrede ableiten",
      });
  }

  // ── Ausweisangaben ──────────────────────────────────────────────────────
  const ausweisFelder: Array<[string | undefined, string]> = [
    [befund.ausweisnummer, "idCardNumber"],
    [befund.gueltigBis, "idCardValidUntil"],
    // Seit 09/2026 haben auch diese drei ein Zielfeld. Vorher wurden sie
    // erkannt und unter „ohne Zielfeld" verworfen — die Erkennung lief, das
    // Ergebnis fiel auf den Boden.
    [befund.ausstellungsdatum, "idCardIssuedAt"],
    [befund.behoerde, "idCardAuthority"],
    [befund.dokumentart, "idCardType"],
    // U6: Die Art des Aufenthaltstitels ist die Unterart des Eintrags.
    [befund.titelart, "idCardSubtype"],
  ];
  for (const [wert, schluessel] of ausweisFelder) {
    if (gesetzt(wert)) werte[schluessel] = wert;
  }

  // ── Angaben ohne Zielfeld ───────────────────────────────────────────────
  const ohneZiel: Array<[string, string | undefined]> = [];
  for (const [label, wert] of ohneZiel) {
    if (gesetzt(wert)) {
      uebergangen.push({ feld: label, wert, grund: "kein Feld in der Kontaktmaske" });
    }
  }

  return { werte, uebergangen };
};

/** Anzeigenamen der Befundfelder für die Prüfliste. */
export const AUSWEIS_LABELS: Record<string, string> = {
  nachname: "Name",
  vornamen: "Vornamen",
  geburtsname: "Geburtsname",
  geburtsdatum: "Geburtsdatum",
  geburtsort: "Geburtsort",
  staatsangehoerigkeit: "Staatsangehörigkeit",
  geschlecht: "Geschlecht",
  ausweisnummer: "Ausweisnummer",
  gueltigBis: "Gültig bis",
  ausstellungsdatum: "Ausstellungsdatum",
  behoerde: "Behörde",
  strasse: "Straße",
  plz: "PLZ",
  ort: "Ort",
  dokumentart: "Dokumentart",
  titelart: "Art des Titels",
};

/**
 * Reihenfolge der Prüfliste — wie auf dem Dokument gelesen: erst die Person,
 * dann die Ausweisdaten, dann die Anschrift von der Rückseite.
 *
 * `mrz` fehlt bewusst: Sie ist Rohmaterial für die Prüfziffern, kein Wert zum
 * Übernehmen.
 *
 * `dokumentart` stand hier bis 09/2026 ebenfalls nicht — sie diente nur dazu,
 * einen Fehlgriff zu erkennen. Seit es das Feld `idCardType` gibt, wird sie
 * übernommen und muss deshalb auch in der Prüfliste stehen: Was gespeichert
 * wird, soll der Anwender vorher sehen.
 */
export const AUSWEIS_REIHENFOLGE: string[] = [
  "nachname",
  "vornamen",
  "geburtsname",
  "geburtsdatum",
  "geburtsort",
  "staatsangehoerigkeit",
  "geschlecht",
  "dokumentart",
  "titelart",
  "ausweisnummer",
  "ausstellungsdatum",
  "gueltigBis",
  "behoerde",
  "strasse",
  "plz",
  "ort",
  "behoerde",
  "ausstellungsdatum",
];

/**
 * Führt Vorder- und Rückseite zusammen (die Anschrift steht hinten, die
 * Personendaten vorn). Wie bei der Visitenkarte laufen die Seiten als
 * getrennte Requests, weil das nginx-Limit pro Request gilt.
 *
 * Die Prüfliste `geprueft` wird VEREINIGT: Die MRZ steht nur auf der
 * Rückseite, ihre Bestätigung muss aber für die Werte der Vorderseite gelten.
 */
export const ausweisSeitenZusammenfuehren = (
  vorderseite: PersonalausweisBefund,
  rueckseite: PersonalausweisBefund = {},
): {
  felder: PersonalausweisBefund;
  konflikte: Array<{ feld: string; vorne: string; hinten: string }>;
} => {
  const felder: PersonalausweisBefund = { ...vorderseite };
  const konflikte: Array<{ feld: string; vorne: string; hinten: string }> = [];

  const hintenGeprueft = new Set(rueckseite.geprueft ?? []);

  for (const [schluessel, hinten] of Object.entries(rueckseite)) {
    if (schluessel === "geprueft") continue;
    if (typeof hinten !== "string" || !gesetzt(hinten)) continue;
    const vorne = (vorderseite as Record<string, unknown>)[schluessel];

    if (typeof vorne !== "string" || !gesetzt(vorne)) {
      (felder as Record<string, string>)[schluessel] = hinten;
      continue;
    }
    if (vorne.toLowerCase() === hinten.toLowerCase()) continue;

    // Weicht die Vorderseite von einem Wert ab, den die MRZ-Prüfziffer
    // BESTÄTIGT hat, ist die Vorderseite verlesen — nicht die Rückseite. Das
    // als Konflikt vorzulegen, hieße den Anwender raten zu lassen, obwohl das
    // Dokument die Antwort selbst mitliefert.
    if (hintenGeprueft.has(schluessel)) {
      (felder as Record<string, string>)[schluessel] = hinten;
      continue;
    }
    konflikte.push({ feld: schluessel, vorne, hinten });
  }

  const geprueft = [...(vorderseite.geprueft ?? []), ...(rueckseite.geprueft ?? [])];
  if (geprueft.length) felder.geprueft = Array.from(new Set(geprueft));

  return { felder, konflikte };
};
